import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { subDays } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBolt,
    faCheck,
    faChevronDown,
    faChevronRight,
    faCircleCheck,
    faClock,
    faCoins,
    faHourglass,
    faWavePulse,
    faTransformerBolt,
} from "@fortawesome/pro-regular-svg-icons";
import toast from "react-hot-toast";

import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import CustomDropdown from "../../components/CustomDropdown";
import DatePicker from "../../components/DatePicker";
import Dropdown from "../../components/Dropdown";
import DataTable from "../../components/DataTable";
import HeaderStatisticsSection from "../../components/HeaderStatisticsSection";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useAuth } from "../../providers/AuthContext";
import { getAllCustomersForSessions } from "../../api/Customers";
import { exportCustomerStatisticsPdf, getCustomerStatistics } from "../../api/ChargingSession";
import { getDateRange, getFriendlyDateRangeI18nKey, getFriendlyDateRangeKey } from "../../helpers/DateTime";

const formatNumber = (n, decimals = 0) => {
    const safe = Number.isFinite(Number(n)) ? Number(n) : 0;
    return safe.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

const formatDuration = (minutes, t) => {
    const safe = Number.isFinite(Number(minutes)) ? Number(minutes) : 0;
    if (safe < 60) {
        return `${formatNumber(safe, 0)} ${t("common.minutes")}`;
    }
    return `${formatNumber(safe / 60, 1)} ${t("customerStatistics.hours")}`;
};

const pluginTone = (percent) => {
    const safe = Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0;
    if (safe >= 85) {
        return { tone: "positive", barPercent: safe, barClass: "bg-secondary-600" };
    }
    if (safe >= 60) {
        return { tone: "caution", barPercent: safe, barClass: "bg-kraft-500" };
    }
    return { tone: "critical", barPercent: safe, barClass: "bg-error-500" };
};

const CellValue = ({ nested, children }) => (
    <span className={nested ? "text-sm tabular-nums" : "text-sm font-bold tabular-nums"}>
        {children}
    </span>
);

const PluginBar = ({ rate }) => {
    const presentation = pluginTone(Number(rate) || 0);
    const textClass = presentation.tone === "positive"
        ? "text-secondary-700"
        : presentation.tone === "caution"
            ? "text-kraft-700"
            : "text-error-600";
    return (
        <div className="flex items-center gap-2">
            <div className="h-3 w-24 rounded-full bg-mute-100">
                <div
                    className={`h-3 rounded-full ${presentation.barClass}`}
                    style={{ width: `${presentation.barPercent}%` }}
                />
            </div>
            <span className={`text-sm font-bold tabular-nums ${textClass}`}>
                {formatNumber(presentation.barPercent, 0)}%
            </span>
        </div>
    );
};

const reasonLabel = (reason, t) => {
    if (!reason) {
        return "";
    }
    return t(`session.zeroEnergy.reason.${reason}`, { defaultValue: reason });
};

const reasonDescription = (reason, t) => {
    if (!reason) {
        return "";
    }
    return t(`session.zeroEnergy.description.${reason}`, { defaultValue: "" });
};

const displayShare = (percent, sessions) => {
    const value = Number(percent) || 0;
    if (value > 0 && value < 1) {
        return 1;
    }
    if (value <= 0 && Number(sessions) > 0) {
        return 1;
    }
    return value;
};

export default function CustomerStatisticsPage() {
    const { t, i18n } = useTranslation();
    const { getAccessToken } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [customersLoaded, setCustomersLoaded] = useState(false);
    const [customerPublicId, setCustomerPublicId] = useState("");
    const [startDate, setStartDate] = useState(() => subDays(new Date(), 7));
    const [endDate, setEndDate] = useState(() => new Date());
    const [statistics, setStatistics] = useState(null);
    const [appliedFilter, setAppliedFilter] = useState(null);
    const [fetching, setFetching] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [expandedTokens, setExpandedTokens] = useState(() => new Set());
    const [errorsExpanded, setErrorsExpanded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const loadCustomers = async () => {
            try {
                const accessToken = await getAccessToken();
                const response = await getAllCustomersForSessions(accessToken);
                const list = Array.isArray(response) ? response : [];
                if (!cancelled) {
                    setCustomers(list.sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? "")));
                }
            } catch (error) {
                console.error("fetchCustomers failed:", error);
                if (!cancelled) {
                    setCustomers([]);
                }
            } finally {
                if (!cancelled) {
                    setCustomersLoaded(true);
                }
            }
        };
        loadCustomers();
        return () => {
            cancelled = true;
        };
    }, [getAccessToken]);

    const summaryItems = useMemo(() => {
        const summary = statistics?.summary;
        const currency = statistics?.currencyCode || "SEK";
        const plugin = pluginTone(Number(summary?.pluginSuccessRate) || 0);
        return [
            { icon: faCoins, title: t("sessions.summary.totalTransactionAmount"), value: `${formatNumber(summary?.totalTransactionAmount, 0)} ${currency}` },
            { icon: faWavePulse, title: t("sessions.summary.totalSessions"), value: formatNumber(summary?.totalSessions, 0) },
            { icon: faCheck, title: t("sessions.summary.successfulSessions"), value: formatNumber(summary?.successfulSessions, 0) },
            { icon: faTransformerBolt, title: t("sessions.summary.energyUsed"), value: `${formatNumber(summary?.totalEnergyCharged, 2)} kWh` },
            {
                icon: faCircleCheck,
                title: t("sessions.summary.pluginSuccessRate"),
                titleHint: t("sessions.summary.pluginSuccessRateHint"),
                value: `${formatNumber(summary?.pluginSuccessRate, 2)}%`,
                tone: plugin.tone,
                barPercent: plugin.barPercent,
            },
            { icon: faClock, title: t("sessions.summary.averageChargingTime"), value: formatDuration(summary?.averageChargingTimeMinutes, t) },
            { icon: faBolt, title: t("sessions.summary.averageEnergyUsed"), value: `${formatNumber(summary?.averageEnergyCharged, 2)} kWh` },
            { icon: faHourglass, title: t("customerStatistics.totalTime"), value: formatDuration(summary?.totalChargingMinutes, t) },
        ];
    }, [statistics, t]);

    const tableRows = useMemo(() => {
        const tokens = statistics?.tokens ?? [];
        const rows = [];
        tokens.forEach((token, index) => {
            const tokenKey = token.tokenName ? token.tokenName : `token-${index}`;
            rows.push({ kind: "token", rowId: tokenKey, token, open: expandedTokens.has(tokenKey) });
            if (expandedTokens.has(tokenKey)) {
                (token.sites ?? []).forEach((site, siteIndex) => {
                    rows.push({
                        kind: "site",
                        rowId: `${tokenKey}-site-${siteIndex}`,
                        site,
                    });
                });
            }
        });
        return rows;
    }, [statistics, expandedTokens]);

    const errors = statistics?.errors ?? [];
    const visibleErrors = errorsExpanded ? errors : errors.slice(0, 3);

    const toggleToken = (tokenKey) => {
        setExpandedTokens((current) => {
            const next = new Set(current);
            if (next.has(tokenKey)) {
                next.delete(tokenKey);
            } else {
                next.add(tokenKey);
            }
            return next;
        });
    };

    const customerOptions = useMemo(
        () => customers
            .filter((customer) => customer?.customerPublicId)
            .map((customer) => ({
                value: customer.customerPublicId,
                label: customer.name || customer.customerPublicId,
            })),
        [customers]
    );

    useEffect(() => {
        if (!customerPublicId || !startDate || !endDate) {
            setStatistics(null);
            setAppliedFilter(null);
            setFetching(false);
            return undefined;
        }

        let cancelled = false;
        const load = async () => {
            const startDateTime = new Date(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate(),
                0,
                0,
                0,
                0
            );
            const endDateTime = new Date(
                endDate.getFullYear(),
                endDate.getMonth(),
                endDate.getDate(),
                23,
                59,
                59,
                999
            );
            const payload = {
                customerPublicId,
                startDateTime: startDateTime.toISOString(),
                endDateTime: endDateTime.toISOString(),
                locale: i18n.language,
            };

            setFetching(true);
            try {
                const accessToken = await getAccessToken();
                const data = await getCustomerStatistics(accessToken, payload);
                if (cancelled) {
                    return;
                }
                setStatistics(data);
                setAppliedFilter(payload);
                setErrorsExpanded(false);
                const firstToken = data?.tokens?.[0];
                const firstKey = firstToken
                    ? (firstToken.tokenName ? firstToken.tokenName : "token-0")
                    : "";
                setExpandedTokens(firstKey ? new Set([firstKey]) : new Set());
            } catch (error) {
                console.error("customer statistics failed:", error);
                if (cancelled) {
                    return;
                }
                setStatistics(null);
                setAppliedFilter(null);
                toast.error(error.message || t("customerStatistics.loadFailed"));
            } finally {
                if (!cancelled) {
                    setFetching(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [customerPublicId, startDate, endDate, getAccessToken, i18n.language, t]);

    const rangeClass = () => {
        const rangeKey = getFriendlyDateRangeKey(startDate, endDate);
        if (["today", "last7days", "last30days", "thisYear"].includes(rangeKey)) {
            return "text-primary-500 font-bold";
        }
        return "text-mute-400";
    };

    const exportPdf = async () => {
        if (!appliedFilter || exporting) {
            return;
        }
        setExporting(true);
        try {
            const accessToken = await getAccessToken();
            const blob = await exportCustomerStatisticsPdf(accessToken, {
                ...appliedFilter,
                locale: i18n.language,
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "customer-statistics.pdf";
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("customer statistics pdf failed:", error);
            toast.error(t("customerStatistics.exportFailed"));
        } finally {
            setExporting(false);
        }
    };

    const columns = [
        {
            key: "name",
            header: t("customerStatistics.token"),
            headerClassName: "min-w-[16rem]",
            cellClassName: "min-w-[16rem]",
            render: (row) => {
                if (row.kind === "site") {
                    return <span className="pl-8 text-sm">{row.site.siteName}</span>;
                }
                const token = row.token;
                const vehicle = [token.registrationNumber, token.driverName].filter(Boolean).join(" · ");
                const name = token.tokenName || t("customerStatistics.unknownToken");
                const share = displayShare(token.energySharePercent, token.totalSessions);
                return (
                    <div className="flex items-start gap-2 border-l-4 border-primary-500 pl-3">
                        <button
                            type="button"
                            className="mt-1 text-primary-500"
                            aria-expanded={row.open}
                            onClick={() => toggleToken(row.rowId)}
                        >
                            <FontAwesomeIcon icon={row.open ? faChevronDown : faChevronRight} className="w-3" />
                        </button>
                        <div className="min-w-0">
                            <div className="text-base font-bold">{name}</div>
                            <div className="text-sm text-mute-800">
                                {[vehicle, t("customerStatistics.sites", { count: token.siteCount ?? 0 })]
                                    .filter(Boolean)
                                    .join(" · ")}
                            </div>
                            <div className="text-sm text-mute-800">
                                {t("customerStatistics.energyShare", { percent: formatNumber(share, 0) })}
                            </div>
                            {row.open && token.topErrorCount > 0 && (
                                <div className="text-sm text-mute-800">
                                    <div>
                                        {t("customerStatistics.topError", {
                                            reason: reasonLabel(token.topErrorReason, t),
                                            count: token.topErrorCount,
                                        })}
                                    </div>
                                    <div>{reasonDescription(token.topErrorReason, t)}</div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            key: "sessions",
            header: t("customerStatistics.sessions"),
            render: (row) => (
                <CellValue nested={row.kind === "site"}>
                    {formatNumber(row.kind === "site" ? row.site.totalSessions : row.token.totalSessions, 0)}
                </CellValue>
            ),
        },
        {
            key: "energy",
            header: t("customerStatistics.energy"),
            render: (row) => (
                <CellValue nested={row.kind === "site"}>
                    {formatNumber(row.kind === "site" ? row.site.totalEnergyCharged : row.token.totalEnergyCharged, 2)}
                </CellValue>
            ),
        },
        {
            key: "time",
            header: t("customerStatistics.time"),
            render: (row) => (
                <CellValue nested={row.kind === "site"}>
                    {formatDuration(row.kind === "site" ? row.site.totalChargingMinutes : row.token.totalChargingMinutes, t)}
                </CellValue>
            ),
        },
        {
            key: "plugin",
            header: t("sessions.summary.pluginSuccessRate"),
            render: (row) => (
                <PluginBar rate={row.kind === "site" ? row.site.pluginSuccessRate : row.token.pluginSuccessRate} />
            ),
        },
        {
            key: "amount",
            header: t("customerStatistics.amount"),
            render: (row) => {
                const amount = row.kind === "site" ? row.site.totalTransactionAmount : row.token.totalTransactionAmount;
                return (
                    <CellValue nested={row.kind === "site"}>
                        {`${formatNumber(amount, 0)} ${statistics?.currencyCode || "SEK"}`}
                    </CellValue>
                );
            },
        },
    ];

    return (
        <>
            <HeaderBar />
            <div className="flex">
                <Sidebar />
                <div className="page">
                    {fetching && (
                        <div className="absolute inset-0 flex items-center justify-center z-[50] pointer-events-none">
                            <div className="bg-white shadow-xl rounded-2xl px-10 py-8 min-w-[300px] flex flex-col items-center gap-4 border border-mute-200">
                                <LoadingSpinner text={t("common.loading")} />
                            </div>
                        </div>
                    )}
                    {exporting && (
                        <div className="absolute inset-0 flex items-center justify-center z-[60]">
                            <div className="bg-white shadow-xl rounded-2xl px-10 py-8 min-w-[280px] flex flex-col items-center gap-4 border border-mute-200">
                                <LoadingSpinner text={t("customerStatistics.exportingPdf")} />
                            </div>
                        </div>
                    )}
                    <div className="page-content">
                        <h2 className="text-2xl text-black font-bold">{t("pages.customerStatistics")}</h2>
                        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                            <div className="w-full max-w-md">
                                <Dropdown
                                    label={t("customerStatistics.customer")}
                                    searchable
                                    options={customerOptions}
                                    selectedOptions={customerPublicId ? [customerPublicId] : []}
                                    managedSelections
                                    disabled={!customersLoaded}
                                    onSelect={(value) => setCustomerPublicId(value || "")}
                                    setSelectedOptions={(values) => setCustomerPublicId(values?.[0] || "")}
                                />
                            </div>
                            <div className="flex flex-col items-end gap-2">
                                <div className="flex flex-wrap items-center justify-end gap-2">
                                <CustomDropdown
                                    isRight={true}
                                    ContentComponent={() => (
                                        <div className="w-40 flex flex-col gap-2 items-start bg-white p-4 rounded-lg">
                                            <button
                                                type="button"
                                                className="text-sm hover:font-bold text-primary-500 hover:text-primary-600"
                                                onClick={() => {
                                                    const today = new Date();
                                                    setStartDate(today);
                                                    setEndDate(today);
                                                }}
                                            >
                                                {t("dateRange.today")}
                                            </button>
                                            <button
                                                type="button"
                                                className="text-sm hover:font-bold text-primary-500 hover:text-primary-600"
                                                onClick={() => {
                                                    const lastWeek = new Date();
                                                    lastWeek.setDate(lastWeek.getDate() - 7);
                                                    setStartDate(lastWeek);
                                                    setEndDate(new Date());
                                                }}
                                            >
                                                {t("dateRange.last7Days")}
                                            </button>
                                            <button
                                                type="button"
                                                className="text-sm hover:font-bold text-primary-500 hover:text-primary-600"
                                                onClick={() => {
                                                    const lastMonth = new Date();
                                                    lastMonth.setMonth(lastMonth.getMonth() - 1);
                                                    setStartDate(lastMonth);
                                                    setEndDate(new Date());
                                                }}
                                            >
                                                {t("dateRange.last30Days")}
                                            </button>
                                            <button
                                                type="button"
                                                className="text-sm hover:font-bold text-primary-500 hover:text-primary-600"
                                                onClick={() => {
                                                    setStartDate(new Date(new Date().getFullYear(), 0, 1));
                                                    setEndDate(new Date());
                                                }}
                                            >
                                                {t("dateRange.thisYear")}
                                            </button>
                                        </div>
                                    )}
                                >
                                    <div className="flex gap-2">
                                        <button type="button" className={`text-sm text-black ${rangeClass()}`}>
                                            {t(getFriendlyDateRangeI18nKey(startDate, endDate))}
                                        </button>
                                    </div>
                                </CustomDropdown>
                                <CustomDropdown
                                    isRight={true}
                                    ContentComponent={() => (
                                        <DatePicker
                                            startDate={startDate}
                                            endDate={endDate}
                                            setStartDate={setStartDate}
                                            setEndDate={setEndDate}
                                        />
                                    )}
                                >
                                    <div className="flex gap-2">
                                        <button type="button" className="text-sm font-normal text-black">
                                            {getDateRange(startDate, endDate, t)}{" "}
                                            <FontAwesomeIcon icon={faChevronDown} />
                                        </button>
                                    </div>
                                </CustomDropdown>
                                </div>
                                <button
                                    type="button"
                                    className="btn-sm btn-outline-primary"
                                    disabled={!appliedFilter || fetching || exporting}
                                    onClick={exportPdf}
                                >
                                    {t("customerStatistics.exportPdf")}
                                </button>
                            </div>
                        </div>

                        {!customerPublicId && (
                            <p className="text-sm">{t("customerStatistics.chooseCustomer")}</p>
                        )}

                        {statistics && (
                            <div>
                                    <HeaderStatisticsSection
                                        items={summaryItems}
                                        className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
                                    />

                                    <div className="surface-card mt-6 mb-8 max-w-xl p-4">
                                        <h3 className="mb-3 text-base font-semibold">{t("customerStatistics.errors")}</h3>
                                        {errors.length === 0 && (
                                            <p className="text-sm text-mute-600">{t("customerStatistics.noErrors")}</p>
                                        )}
                                        <div className="flex flex-col gap-3">
                                            {visibleErrors.map((error) => {
                                                const share = Math.min(100, Math.max(0, Number(error.sharePercent) || 0));
                                                return (
                                                    <div key={error.reason}>
                                                        <div className="flex justify-between gap-3 text-sm">
                                                            <span className="font-semibold">{reasonLabel(error.reason, t)}</span>
                                                            <span className="shrink-0 tabular-nums text-mute-600">
                                                                {error.count} · {formatNumber(share, 0)}%
                                                            </span>
                                                        </div>
                                                        <p className="mt-1 text-sm text-mute-800">{reasonDescription(error.reason, t)}</p>
                                                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-mute-100">
                                                            <div
                                                                className="h-1.5 rounded-full bg-primary-500"
                                                                style={{ width: `${share}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {errors.length > 3 && (
                                            <button
                                                type="button"
                                                className="mt-3 text-sm text-primary-500"
                                                onClick={() => setErrorsExpanded((open) => !open)}
                                            >
                                                {errorsExpanded
                                                    ? t("customerStatistics.showLess")
                                                    : t("customerStatistics.showMore")}
                                            </button>
                                        )}
                                    </div>

                                    <DataTable
                                        rows={tableRows}
                                        columns={columns}
                                        rowKey={(row) => row.rowId}
                                        rowClassName={(row) => (row.kind === "site" ? "bg-primary-50" : "bg-white")}
                                        tableClassName="w-full"
                                        containerClassName="w-full min-w-0 overflow-x-auto rounded-2xl border border-mute-200 shadow-md"
                                        defaultHeaderClassName="bg-primary-500 px-4 py-3 text-left text-sm font-bold text-white"
                                        defaultCellClassName="border-b border-primary-100 px-4 py-3 align-middle"
                                        showPagination={false}
                                        defaultRowsPerPage={1000}
                                        emptyContent={t("customerStatistics.empty")}
                                    />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
